import React, { useEffect, useRef, useState, useMemo } from "react";
import { snakeCase } from "lodash";
import fieldPropTypes from "./field-prop-types";
import renderFields from "../screens/render-fields";
import {
  getFieldActionEventStates,
  actionEventsMap
} from "../screens/helpers/action-event-states";
import { fieldValidation } from "../utils/field-validation";
import { LAYOUT_IDS, ARIA_ROLES, FIELD_TYPES } from "../utils/constants";

const Container = ({ field, appData, components, screen }) => {
  const detectWebLayout = layouts =>
    layouts.find(layout => layout["layout-id"] === LAYOUT_IDS.primary);

  const [additionalClassName, setAdditionalClassName] = useState([]);
  const fieldRef = useRef();
  const { fieldLayouts } = field;
  const containerFields = fieldLayouts
    ? detectWebLayout(fieldLayouts)?.containerFields
    : null;
  const hideContainer =
    field?.layout?.visible === false || !containerFields?.length;

  useEffect(() => {
    if (!hideContainer) {
      fieldValidation.container.update(field, appData, { fieldRef });
    }
  }, [appData, appData.screenValidation, field, hideContainer]);

  const onClickEventStates = getFieldActionEventStates(
    field,
    actionEventsMap.onClick
  );
  const actionType = field?.action?.type;

  useEffect(
    () =>
      containerFields?.forEach(f => {
        const classNames = [];

        classNames.push(
          f.type === "containerCarousel" && f.fieldLayouts.length > 1
            ? "carousel"
            : ""
        );
        classNames.push(f.name === "labelOptionsTitle" ? "productTile" : "");
        classNames.push(
          f.name === "labelAvailabilityValue" ? "hasLabelAvailability" : ""
        );
        classNames.push(f.name === "caret" ? "hasRightArrow" : "");
        classNames.push(
          f.image === "ic_calendar_pulse"
            ? "isCalenderPulse"
            : f.image === "ic_pulse_check_circle_active"
            ? "icPulseCheckCircleActive"
            : ""
        );
        classNames.push(f.name === "subtitle_2" ? "hasSubtitle_2" : "");

        setAdditionalClassName(prevState => [...prevState, ...classNames]);
      }),
    []
  );

  const experiments = useMemo(
    () =>
      !field.data?.experiments
        ? {}
        : Object.keys(field.data.experiments).reduce((acc, cur) => {
            const key = `data-${snakeCase(cur)}`;
            return { ...acc, [key]: field.data.experiments[cur] };
          }, {}),
    [field]
  );

  if (hideContainer) {
    return null;
  }

  const handleContainerClick = event => {
    event.stopPropagation();
    const { name, text, action } = field;

    if (actionType || onClickEventStates?.length) {
      appData.handleButtonClick(
        name,
        text,
        action,
        0,
        undefined,
        onClickEventStates
      );
    }
  };

  const handleKeyPress = event => {
    if (event.keyCode === 13 || event.keyCode === 32)
      return handleContainerClick(event);
  };

  const nestedContent = renderFields(
    containerFields,
    appData,
    components,
    screen
  );
  const isRadioContainerChecked = fieldObj =>
    fieldObj?.fieldLayouts?.[0]?.containerFields.find(
      f => f?.type === FIELD_TYPES.formRadioButtons
    )?.options?.[0]?.selected;

  const careOptionTileProps = () => {
    if (field.name.includes("careOptionTile")) {
      return {
        id: field.name,
        "aria-labelledby": field.name,
        role: "link"
      };
    }
  };

  const { Container: ContainerComponent } = components;
  const ariaProps = field.accessibility
    ? {
        ...field.accessibility,
        ...(field.accessibility?.role === ARIA_ROLES.radio && {
          "aria-checked": isRadioContainerChecked(field)
        })
      }
    : {};

  const isDisabled = field.params
    ?.split("|")
    .includes("TDFieldOptionIsInactive");

  const containerButtonProps =
    actionType || onClickEventStates?.length || isDisabled
      ? {
          role: ariaProps.role || "button",
          tabIndex: isDisabled ? null : "0",
          disabled: isDisabled,
          onKeyDown: isDisabled ? null : handleKeyPress,
          onClick: isDisabled ? null : handleContainerClick,
          ...careOptionTileProps()
        }
      : {};

  return (
    <ContainerComponent
      field={field}
      containerButtonProps={containerButtonProps}
      experiments={experiments}
      ariaProps={ariaProps}
      fieldRef={fieldRef}
      additionalClassName={additionalClassName}
    >
      {nestedContent}
    </ContainerComponent>
  );
};

Container.propTypes = fieldPropTypes;

export default Container;
